import React, { useState, useRef } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import Avatar from '../../../common/Avatar';
import authService from '../../../../services/authService';
import { getProfileImageSrc, forceImageRefresh } from '../../../../config/defaultImages';

const ProfileImageUpload = ({ user, onImageUpdate, showAvatarOnly = false, showAsButton = false }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Por favor selecciona un archivo de imagen válido (JPG, PNG, GIF)');
      toast.error('Por favor selecciona un archivo de imagen válido (JPG, PNG, GIF)');
      return;
    }

    // Validar tamaño (5MB máximo)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError('El archivo es demasiado grande. El tamaño máximo es 5MB');
      toast.error('El archivo es demasiado grande. El tamaño máximo es 5MB');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();
      formData.append('image', file);

      const response = await authService.uploadProfileImage(user.id, formData);
      
      if (response.success && response.data?.profileImageUrl) {
        // Forzar una única actualización global de imágenes
        forceImageRefresh();
        
        // Actualizar el contexto con el usuario completo si está disponible
        if (response.data.user) {
          onImageUpdate?.(response.data.user);
        } else {
          onImageUpdate?.(response.data.profileImageUrl);
        }
        
        // El toast ya se muestra en el servicio
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      const errorMessage = error.response?.data?.message || 'Error al subir la imagen';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setUploading(false);
      // Limpiar el input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Modo botón normal - MUY VISIBLE
  if (showAsButton) {
    return (
      <>
        <Button
          variant="primary"
          size="sm"
          onClick={handleFileSelect}
          disabled={uploading}
          className="me-2"
        >
          {uploading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Subiendo...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon="camera" className="me-2" />
              Cambiar Foto de Perfil
            </>
          )}
        </Button>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {error && (
          <Alert variant="danger" className="py-2 fs-10 mt-2">
            {error}
          </Alert>
        )}
      </>
    );
  }

  // Si es modo showAvatarOnly, solo mostrar el botón de editar
  if (showAvatarOnly) {
    return (
      <>
        <Button
          variant="primary"
          size="sm"
          className="rounded-circle shadow border-3 border-white"
          onClick={handleFileSelect}
          disabled={uploading}
          style={{
            width: '42px',
            height: '42px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: '3px solid white',
            boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
            transition: 'all 0.3s ease'
          }}
          title="Cambiar foto de perfil"
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
            e.target.style.boxShadow = '0 8px 20px rgba(0,0,0,0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
          }}
        >
          {uploading ? (
            <Spinner size="sm" style={{ width: '16px', height: '16px' }} />
          ) : (
            <FontAwesomeIcon icon="pen" className="text-white" style={{ fontSize: '14px' }} />
          )}
        </Button>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </>
    );
  }

  // Modo completo (no se usa actualmente)
  return (
    <div className="text-center">
      <div className="position-relative d-inline-block mb-3">
        <Avatar
          size="4xl"
          src={getProfileImageSrc(user)}
          name={user.fullName}
          className="border border-3 border-white shadow"
        />
        
        <Button
          variant="primary"
          size="sm"
          className="position-absolute rounded-circle p-2 shadow"
          style={{
            bottom: '5px',
            right: '5px',
            width: '35px',
            height: '35px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={handleFileSelect}
          disabled={uploading}
        >
          {uploading ? (
            <Spinner size="sm" />
          ) : (
            <FontAwesomeIcon icon="pen" className="fs-10" />
          )}
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="py-2 fs-10">
          {error}
        </Alert>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <div className="text-muted fs-10">
        <small>JPG, JPEG, PNG, GIF</small><br />
        <small>Máximo 5MB</small>
      </div>
    </div>
  );
};

export default ProfileImageUpload; 